import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { organizationDataAtom, campaignsDataAtom } from '@/store';
import {
  businessQueryOptions,
  useCampaignAllStateApproveMutationOptions,
  useCampaignBudgetMutationOptions,
} from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useCampaignStateTransition } from '@/hooks/useCampaignStateTransition';
import { ownsTransitionSpinner } from '@/lib/transition-ownership';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useToast } from '@/hooks/useToast';
import { CAMPAIGN_STATE, type CampaignData } from '@/lib/types';

/**
 * Shared "deploy pending changes to Google Ads" logic for a campaign.
 *
 * Used by both the campaign detail page and the ad detail page so the deploy
 * trigger (`hasPendingChanges`) and the deploy action can never diverge between
 * the two surfaces — the same divergence that previously split the toolbar
 * Deploy button from the "Campaign Changes Pending" banner (#5583).
 */
export function useDeployChanges(campaign: CampaignData, isStillGenerating: boolean = false) {
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { symbol } = useCurrencySymbol();
  const organizationData = useAtomValue(organizationDataAtom);
  const allCampaigns = useAtomValue(campaignsDataAtom);

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Google daily budget is the ceiling campaigns are validated against on deploy (#4683).
  const googleBudgetEntry = business?.platform_budgets?.find((b) => b.platform === 'Google');
  const googleDailyBudget = googleBudgetEntry ? Math.floor(googleBudgetEntry.amount / 30) : 0;
  const totalDailyBudget = allCampaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
  const isOverGoogleBudget = googleDailyBudget > 0 && totalDailyBudget > googleDailyBudget;

  const hasNoAds = !campaign.ads || campaign.ads.length === 0;

  // Google Ads Responsive Search Ad minimums (3 headlines, 2 descriptions). Deploying a campaign
  // with an ad below these gets rejected by the sync, so block it with a clear reason instead of
  // a doomed ~15s deploy that errors and leaves the campaign in the same pending state (#5598).
  const MIN_HEADLINES = 3;
  const MIN_DESCRIPTIONS = 2;
  const adBelowMinimums = (campaign.ads ?? []).find(
    (a) => (a.headlines?.length ?? 0) < MIN_HEADLINES || (a.descriptions?.length ?? 0) < MIN_DESCRIPTIONS,
  );
  const minimumsBlockReason = adBelowMinimums
    ? `${adBelowMinimums.name || 'An ad'} needs at least ${MIN_HEADLINES} headlines and ${MIN_DESCRIPTIONS} descriptions before deploying.`
    : undefined;

  // Pending = a LIVE campaign with a draft flag (new ad / budget / settings / assets), or a
  // previously-published campaign back in DRAFT, with a real budget set.
  const hasPendingChanges =
    ((campaign.state === CAMPAIGN_STATE.LIVE &&
      (campaign.budget_state === 'draft' ||
        campaign.assets_state === 'draft' ||
        campaign.settings_state === 'draft')) ||
      (campaign.state === CAMPAIGN_STATE.DRAFT && campaign.first_published_at)) &&
    (campaign.budget ?? 0) > 0;

  const campaignAllStateApproveMutation = useCampaignAllStateApproveMutationOptions(campaign.slug);
  const campaignBudgetMutation = useCampaignBudgetMutationOptions();
  // Use the polling state-transition (like go-live) so isPending stays true until the launch
  // task is CONFIRMED complete, not just dispatched — otherwise the button flashes back to
  // "Deploy" during the ~15s async sync window (#5583).
  const { dispatch: dispatchStateChange, isPending: isStateTransitionPending, pendingSource } = useCampaignStateTransition(
    campaign.id,
    campaign.state as CAMPAIGN_STATE | undefined,
    'deploy_button',
  );

  const [isRunning, setIsRunning] = useState(false);
  // Spin only for deploys this button owns (its own dispatches, plus transitions resumed
  // after a reload) — a go_live_button / approval_flow transition must not materialize
  // this button (#5663, ownership rules in transition-ownership.ts).
  const isDeploying = isRunning || (isStateTransitionPending && ownsTransitionSpinner('deploy_button', pendingSource));
  // A state transition dispatched elsewhere (Go Live toolbar button, approval flow,
  // pause toggle) is in flight. During a first go-live the campaign transiently
  // satisfies hasPendingChanges (LIVE + draft flags / DRAFT + first_published_at are
  // committed at task start), which materialized Deploy beside "Going Live..." (#5825).
  // Render gates use this to keep Deploy unmounted until the foreign transition lands.
  const isForeignTransitionPending =
    isStateTransitionPending && !ownsTransitionSpinner('deploy_button', pendingSource);
  const deployDisabled = hasNoAds || isOverGoogleBudget || isDeploying;
  const overBudgetTitle = isOverGoogleBudget
    ? `Campaigns total ${symbol}${totalDailyBudget.toLocaleString()}/day, over your ${symbol}${googleDailyBudget.toLocaleString()}/day Google budget.`
    : undefined;

  const deploy = async () => {
    // Block-with-reason while any generation task is in flight (#5637). The button stays
    // visible and enabled as a reminder to deploy once creation finishes; the click tells
    // the user why nothing happened instead of silently no-oping or firing a doomed deploy.
    if (isStillGenerating) {
      toast({ description: 'Campaign is still generating, please wait', variant: 'destructive' });
      return;
    }
    if (hasNoAds) {
      toast({ description: 'Create at least one ad before deploying', variant: 'destructive' });
      return;
    }
    if (minimumsBlockReason) {
      toast({ description: minimumsBlockReason, variant: 'destructive' });
      return;
    }
    setIsRunning(true);
    try {
      await campaignAllStateApproveMutation.mutateAsync();
      // approve_all_states excludes budget_state, so force it APPROVED here (like go-live).
      // Without `approve: true` the budget stays DRAFT until the async launch sync clears it,
      // leaving hasPendingChanges true and the Deploy button flickering (#5583).
      await campaignBudgetMutation.mutateAsync({
        campaignId: campaign.id,
        budgetUpdate: { budget: campaign.budget, approve: true },
      });
      // Fire-and-poll: dispatch resolves on accept; isStateTransitionPending then tracks the
      // launch task to completion so the button stays "Deploying..." until it's actually done.
      await dispatchStateChange(CAMPAIGN_STATE.LIVE);
      await refetchCampaign();
    } catch (error) {
      await refetchCampaign();
      toast({
        description: error instanceof Error ? error.message : 'Failed to deploy changes',
        variant: 'destructive',
      });
    } finally {
      setIsRunning(false);
    }
  };

  return { hasPendingChanges, deploy, isDeploying, deployDisabled, overBudgetTitle, isOverGoogleBudget, isForeignTransitionPending };
}
